import { deriveSeed, type SeedSource } from "./seed.js";

export const AUDIO_LAYERS = Object.freeze([
  "wind",
  "waves",
  "whiteNoise",
  "chimes",
] as const);

export const CHIME_SEED_NAMESPACE = "audio/v1" as const;

export type AudioLayer = (typeof AUDIO_LAYERS)[number];
export type AudioStatus = "off" | "enabling" | "on" | "disabling";

export interface AudioLayerState {
  readonly wind: boolean;
  readonly waves: boolean;
  readonly whiteNoise: boolean;
  readonly chimes: boolean;
}

export interface ProceduralAudioSnapshot {
  readonly status: AudioStatus;
  readonly message: string;
  readonly layers: AudioLayerState;
}

export interface ChimeEvent {
  readonly delaySeconds: number;
  readonly frequency: number;
}

interface AudioParamBoundary {
  value: number;
  cancelScheduledValues(time: number): void;
  setValueAtTime(value: number, time: number): void;
  linearRampToValueAtTime(value: number, time: number): void;
  exponentialRampToValueAtTime(value: number, time: number): void;
}

interface AudioNodeBoundary {
  connect(destination: AudioNodeBoundary | AudioParamBoundary): unknown;
  disconnect(): void;
}

interface GainNodeBoundary extends AudioNodeBoundary {
  readonly gain: AudioParamBoundary;
}

interface OscillatorNodeBoundary extends AudioNodeBoundary {
  type: OscillatorType;
  readonly frequency: AudioParamBoundary;
  onended: (() => void) | null;
  start(time?: number): void;
  stop(time?: number): void;
}

interface BufferSourceNodeBoundary extends AudioNodeBoundary {
  buffer: AudioBufferBoundary | null;
  loop: boolean;
  start(time?: number): void;
  stop(time?: number): void;
}

interface BiquadFilterNodeBoundary extends AudioNodeBoundary {
  type: BiquadFilterType;
  readonly frequency: AudioParamBoundary;
  readonly Q: AudioParamBoundary;
}

interface AudioBufferBoundary {
  getChannelData(channel: number): Float32Array;
}

export interface AudioContextBoundary {
  readonly currentTime: number;
  readonly sampleRate: number;
  readonly destination: AudioNodeBoundary;
  readonly state: string;
  createGain(): GainNodeBoundary;
  createOscillator(): OscillatorNodeBoundary;
  createBufferSource(): BufferSourceNodeBoundary;
  createBiquadFilter(): BiquadFilterNodeBoundary;
  createBuffer(channels: number, length: number, sampleRate: number): AudioBufferBoundary;
  resume(): Promise<void>;
  close(): Promise<void>;
}

export type AudioTimerHandle = ReturnType<typeof globalThis.setTimeout>;

export interface AudioScheduler {
  setTimeout(callback: () => void, delayMs: number): AudioTimerHandle;
  clearTimeout(handle: AudioTimerHandle): void;
}

export interface CreateProceduralAudioControllerOptions {
  readonly seed: SeedSource;
  readonly createContext?: () => AudioContextBoundary;
  readonly scheduler?: AudioScheduler;
  readonly waitForRamp?: (milliseconds: number) => Promise<void>;
  readonly initialLayers?: Partial<Record<AudioLayer, boolean>>;
}

export interface ProceduralAudioController {
  readonly snapshot: ProceduralAudioSnapshot;
  enableFromGesture(): Promise<boolean>;
  disable(): Promise<void>;
  setLayerEnabled(layer: AudioLayer, enabled: boolean): void;
  subscribe(listener: (snapshot: ProceduralAudioSnapshot) => void): () => void;
}

interface EnableAttempt {
  readonly token: number;
  readonly context: AudioContextBoundary;
  cancelled: boolean;
  closeComplete: boolean;
  settlementComplete: boolean;
  readonly settlementPromise: Promise<void>;
  readonly resolveSettlement: () => void;
}

interface MasterCleanupProgress {
  cancelComplete: boolean;
  setComplete: boolean;
  rampComplete: boolean;
  waitComplete: boolean;
}

const MASTER_LEVEL = 0.42;
const RAMP_SECONDS = 0.08;
const UINT32_RANGE = 0x1_0000_0000;
const NUMERIC_SEED_TAG = 0x9e37_79b9;
const STRING_SEED_TAG = 0x243f_6a88;
const STREAM_TAGS = Object.freeze({
  chimes: 0x1319_8a2e,
  wind: 0x0370_7344,
  noise: 0xa409_3822,
} as const);
const CHIME_FREQUENCIES = Object.freeze([
  523.25,
  587.33,
  659.25,
  783.99,
  880,
] as const);

const LAYER_LEVELS: Readonly<Record<AudioLayer, number>> = Object.freeze({
  wind: 0.19,
  waves: 0.16,
  whiteNoise: 0.08,
  chimes: 0.24,
});

const defaultScheduler: AudioScheduler = Object.freeze({
  setTimeout(callback: () => void, delayMs: number): AudioTimerHandle {
    return globalThis.setTimeout(callback, delayMs);
  },
  clearTimeout(handle: AudioTimerHandle): void {
    globalThis.clearTimeout(handle);
  },
});

const defaultWaitForRamp = (milliseconds: number): Promise<void> => new Promise((resolve) => {
  globalThis.setTimeout(resolve, milliseconds);
});

const defaultCreateContext = (): AudioContextBoundary => {
  const ContextConstructor = globalThis.AudioContext;
  return new ContextConstructor() as unknown as AudioContextBoundary;
};

type AudioSeedStream = keyof typeof STREAM_TAGS;
type Uint32Source = () => number;

const mixUint32 = (value: number): number => {
  let mixed = value >>> 0;
  mixed = Math.imul(mixed ^ (mixed >>> 16), 0x7feb_352d) >>> 0;
  mixed = Math.imul(mixed ^ (mixed >>> 15), 0x846c_a68b) >>> 0;
  return (mixed ^ (mixed >>> 16)) >>> 0;
};

const deriveAudioStreamSeed = (seed: SeedSource, stream: AudioSeedStream): number => {
  const root = deriveSeed(CHIME_SEED_NAMESPACE, seed);
  const sourceTag = typeof seed === "number" ? NUMERIC_SEED_TAG : STRING_SEED_TAG;
  return mixUint32(root ^ sourceTag ^ STREAM_TAGS[stream]);
};

const createRandom = (seed: number): Uint32Source => {
  let state = seed >>> 0;
  return (): number => {
    state = (state + 0x6d2b_79f5) >>> 0;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1) >>> 0;
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return (value ^ (value >>> 14)) >>> 0;
  };
};

const nextBoundedUint32 = (random: Uint32Source, bound: number): number => {
  const limit = UINT32_RANGE - (UINT32_RANGE % bound);
  let value: number;
  do {
    value = random();
  } while (value >= limit);
  return value % bound;
};

const nextChimeEvent = (random: Uint32Source): ChimeEvent => {
  const delaySeconds = 12 + nextBoundedUint32(random, 17);
  const frequencyIndex = nextBoundedUint32(random, CHIME_FREQUENCIES.length);
  return Object.freeze({
    delaySeconds,
    frequency: CHIME_FREQUENCIES[frequencyIndex] ?? CHIME_FREQUENCIES[0],
  });
};

export const createChimeSequence = (
  seed: SeedSource,
  count: number,
): readonly ChimeEvent[] => {
  if (!Number.isSafeInteger(count) || count < 0 || count > 10_000) {
    throw new RangeError("Chime event count must be an integer from 0 through 10000");
  }
  const random = createRandom(deriveAudioStreamSeed(seed, "chimes"));
  return Object.freeze(Array.from({ length: count }, () => nextChimeEvent(random)));
};

const makeLayerState = (
  initial: Partial<Record<AudioLayer, boolean>> | undefined,
): Record<AudioLayer, boolean> => ({
  wind: initial?.wind ?? true,
  waves: initial?.waves ?? true,
  whiteNoise: initial?.whiteNoise ?? true,
  chimes: initial?.chimes ?? true,
});

const makeSnapshot = (
  status: AudioStatus,
  message: string,
  layers: Record<AudioLayer, boolean>,
): ProceduralAudioSnapshot => Object.freeze({
  status,
  message,
  layers: Object.freeze({ ...layers }),
});

const fillNoiseBuffer = (
  buffer: AudioBufferBoundary,
  random: Uint32Source,
  smoothing: number,
): void => {
  const data = buffer.getChannelData(0);
  let previous = 0;
  for (let index = 0; index < data.length; index += 1) {
    const sample = (random() / UINT32_RANGE) * 2 - 1;
    previous = previous * smoothing + sample * (1 - smoothing);
    data[index] = previous;
  }
};

const safeStop = (
  source: OscillatorNodeBoundary | BufferSourceNodeBoundary,
  time: number,
): boolean => {
  try {
    source.stop(time);
    return true;
  } catch {
    // A source that already ended still remains owned until graph cleanup.
    return false;
  }
};

export const createProceduralAudioController = (
  options: CreateProceduralAudioControllerOptions,
): ProceduralAudioController => {
  const createContext = options.createContext ?? defaultCreateContext;
  const scheduler = options.scheduler ?? defaultScheduler;
  const waitForRamp = options.waitForRamp ?? defaultWaitForRamp;
  const layers = makeLayerState(options.initialLayers);
  const listeners = new Set<(snapshot: ProceduralAudioSnapshot) => void>();
  const ownedNodes = new Set<AudioNodeBoundary>();
  const sources = new Set<OscillatorNodeBoundary | BufferSourceNodeBoundary>();
  const startedSources = new Set<OscillatorNodeBoundary | BufferSourceNodeBoundary>();
  const chimeSources = new Set<OscillatorNodeBoundary>();
  const layerGains = new Map<AudioLayer, GainNodeBoundary>();
  const disconnectedNodes = new WeakSet<AudioNodeBoundary>();
  const chimeStreamSeed = deriveAudioStreamSeed(options.seed, "chimes");
  const windStreamSeed = deriveAudioStreamSeed(options.seed, "wind");
  const noiseStreamSeed = deriveAudioStreamSeed(options.seed, "noise");

  let currentSnapshot = makeSnapshot("off", "Off", layers);
  let context: AudioContextBoundary | null = null;
  let pendingAttempt: EnableAttempt | null = null;
  let cleanupAttempt: EnableAttempt | null = null;
  let cleanupContext: AudioContextBoundary | null = null;
  let cleanupContextClosed = false;
  let masterCleanup: MasterCleanupProgress | null = null;
  let masterGain: GainNodeBoundary | null = null;
  const chimeTimers = new Set<AudioTimerHandle>();
  let chimeScheduleGeneration = 0;
  let chimeRandom = createRandom(chimeStreamSeed);
  let pendingChimeEvent: ChimeEvent | null = null;
  let operation = 0;
  let enablePromise: Promise<boolean> | null = null;
  let disablePromise: Promise<void> | null = null;

  const publish = (status: AudioStatus, message: string): void => {
    currentSnapshot = makeSnapshot(status, message, layers);
    for (const listener of [...listeners]) {
      try {
        listener(currentSnapshot);
      } catch {
        listeners.delete(listener);
      }
    }
  };

  const own = <Node extends AudioNodeBoundary>(node: Node): Node => {
    ownedNodes.add(node);
    return node;
  };

  const ownSource = <Source extends OscillatorNodeBoundary | BufferSourceNodeBoundary>(
    source: Source,
  ): Source => {
    own(source);
    sources.add(source);
    return source;
  };

  const startSource = <Source extends OscillatorNodeBoundary | BufferSourceNodeBoundary>(
    source: Source,
    time: number,
  ): void => {
    source.start(time);
    startedSources.add(source);
  };

  const disconnectOnce = (
    node: AudioNodeBoundary,
    errors?: unknown[],
  ): boolean => {
    if (disconnectedNodes.has(node)) return true;
    try {
      node.disconnect();
      disconnectedNodes.add(node);
      return true;
    } catch (error) {
      errors?.push(error);
      return false;
    }
  };

  const cancelChimeSchedule = (errors?: unknown[]): void => {
    chimeScheduleGeneration += 1;
    for (const handle of [...chimeTimers]) {
      try {
        scheduler.clearTimeout(handle);
        chimeTimers.delete(handle);
      } catch (error) {
        errors?.push(error);
      }
    }
  };

  const synthesizeChime = (event: ChimeEvent): void => {
    if (context === null || currentSnapshot.status !== "on" || !layers.chimes) return;
    const chimeGain = layerGains.get("chimes");
    if (chimeGain === undefined) return;

    const now = context.currentTime;
    let oscillator: OscillatorNodeBoundary | null = null;
    let envelope: GainNodeBoundary | null = null;
    let outputReleased = false;
    let sourceReleased = false;
    let started = false;

    const releaseOutput = (): void => {
      if (outputReleased) return;
      outputReleased = true;
      if (oscillator !== null) {
        if (disconnectOnce(oscillator)) ownedNodes.delete(oscillator);
      }
      if (envelope !== null) {
        if (disconnectOnce(envelope)) ownedNodes.delete(envelope);
      }
    };

    const releaseSource = (): void => {
      if (sourceReleased) return;
      sourceReleased = true;
      if (oscillator !== null) {
        oscillator.onended = null;
        chimeSources.delete(oscillator);
        sources.delete(oscillator);
        startedSources.delete(oscillator);
      }
    };

    const releaseAll = (): void => {
      releaseOutput();
      releaseSource();
    };

    const retainSourceForShutdown = (): void => {
      releaseOutput();
      if (oscillator !== null) oscillator.onended = releaseAll;
    };

    try {
      oscillator = ownSource(context.createOscillator());
      chimeSources.add(oscillator);
      envelope = own(context.createGain());
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(event.frequency, now);
      envelope.gain.setValueAtTime(0.0001, now);
      envelope.gain.exponentialRampToValueAtTime(0.7, now + 0.025);
      envelope.gain.exponentialRampToValueAtTime(0.0001, now + 2.4);
      oscillator.connect(envelope);
      envelope.connect(chimeGain);
      oscillator.onended = releaseAll;
      startSource(oscillator, now);
      started = true;
      oscillator.stop(now + 2.5);
    } catch {
      const stopped = oscillator === null || !started || safeStop(oscillator, now);
      if (oscillator !== null && stopped) startedSources.delete(oscillator);
      if (oscillator !== null && started && !stopped) {
        retainSourceForShutdown();
      } else {
        releaseAll();
      }
    }
  };

  const scheduleNextChime = (): void => {
    cancelChimeSchedule();
    if (currentSnapshot.status !== "on" || !layers.chimes) return;
    pendingChimeEvent ??= nextChimeEvent(chimeRandom);
    const event = pendingChimeEvent;
    const generation = chimeScheduleGeneration;
    const handle = scheduler.setTimeout(() => {
      chimeTimers.delete(handle);
      if (generation !== chimeScheduleGeneration) return;
      pendingChimeEvent = null;
      synthesizeChime(event);
      scheduleNextChime();
    }, event.delaySeconds * 1_000);
    chimeTimers.add(handle);
  };

  const buildGraph = (audioContext: AudioContextBoundary): void => {
    const now = audioContext.currentTime;
    const master = own(audioContext.createGain());
    masterGain = master;
    master.gain.setValueAtTime(0, now);
    master.gain.linearRampToValueAtTime(MASTER_LEVEL, now + RAMP_SECONDS);
    master.connect(audioContext.destination);

    for (const layer of AUDIO_LAYERS) {
      const gain = own(audioContext.createGain());
      gain.gain.setValueAtTime(layers[layer] ? LAYER_LEVELS[layer] : 0, now);
      gain.connect(master);
      layerGains.set(layer, gain);
    }

    const windBuffer = audioContext.createBuffer(
      1,
      Math.max(1, Math.floor(audioContext.sampleRate * 2)),
      audioContext.sampleRate,
    );
    fillNoiseBuffer(
      windBuffer,
      createRandom(windStreamSeed),
      0.94,
    );
    const windSource = ownSource(audioContext.createBufferSource());
    const windFilter = own(audioContext.createBiquadFilter());
    windSource.buffer = windBuffer;
    windSource.loop = true;
    windFilter.type = "lowpass";
    windFilter.frequency.setValueAtTime(720, now);
    windFilter.Q.setValueAtTime(0.7, now);
    windSource.connect(windFilter);
    windFilter.connect(layerGains.get("wind") as GainNodeBoundary);

    const wave = ownSource(audioContext.createOscillator());
    const waveMotion = ownSource(audioContext.createOscillator());
    const waveOutput = own(audioContext.createGain());
    const waveMotionDepth = own(audioContext.createGain());
    wave.type = "sine";
    wave.frequency.setValueAtTime(74, now);
    waveMotion.type = "sine";
    waveMotion.frequency.setValueAtTime(0.11, now);
    waveOutput.gain.setValueAtTime(1, now);
    waveMotionDepth.gain.setValueAtTime(0.07, now);
    wave.connect(waveOutput);
    waveOutput.connect(layerGains.get("waves") as GainNodeBoundary);
    waveMotion.connect(waveMotionDepth);
    waveMotionDepth.connect(waveOutput.gain);

    const noiseBuffer = audioContext.createBuffer(
      1,
      Math.max(1, Math.floor(audioContext.sampleRate * 2)),
      audioContext.sampleRate,
    );
    fillNoiseBuffer(
      noiseBuffer,
      createRandom(noiseStreamSeed),
      0,
    );
    const noiseSource = ownSource(audioContext.createBufferSource());
    noiseSource.buffer = noiseBuffer;
    noiseSource.loop = true;
    noiseSource.connect(layerGains.get("whiteNoise") as GainNodeBoundary);

    startSource(windSource, now);
    startSource(wave, now);
    startSource(waveMotion, now);
    startSource(noiseSource, now);
  };

  const disconnectOwnedNodes = (errors?: unknown[]): void => {
    for (const source of chimeSources) source.onended = null;
    for (const node of [...ownedNodes]) {
      if (disconnectOnce(node, errors)) ownedNodes.delete(node);
    }
    pendingChimeEvent = null;
  };

  const performEnable = async (token: number): Promise<boolean> => {
    let attempt: EnableAttempt | null = null;
    let rollbackStarted = false;
    try {
      const candidate = createContext();
      let resolveSettlement: () => void = () => undefined;
      const settlementPromise = new Promise<void>((resolve) => {
        resolveSettlement = resolve;
      });
      attempt = {
        token,
        context: candidate,
        cancelled: false,
        closeComplete: false,
        settlementComplete: false,
        settlementPromise,
        resolveSettlement,
      };
      pendingAttempt = attempt;
      let resumeFailed = false;
      let resumeError: unknown;
      try {
        if (candidate.state !== "running") await candidate.resume();
      } catch (error) {
        resumeFailed = true;
        resumeError = error;
      }
      attempt.settlementComplete = true;
      attempt.resolveSettlement();
      if (resumeFailed) throw resumeError;
      if (
        operation !== token
        || currentSnapshot.status !== "enabling"
        || pendingAttempt !== attempt
      ) {
        rollbackStarted = true;
        await rollbackAttempt(attempt, "Cleaning up cancelled audio enable...");
        return false;
      }
      pendingAttempt = null;
      context = candidate;
      chimeRandom = createRandom(chimeStreamSeed);
      pendingChimeEvent = null;
      buildGraph(candidate);
      publish("on", "On");
      if (operation !== token || context !== candidate) {
        return false;
      }
      scheduleNextChime();
      return true;
    } catch (enableError) {
      if (rollbackStarted) throw enableError;
      if (attempt !== null && pendingAttempt === attempt) pendingAttempt = null;
      if (attempt === null) {
        if (operation === token) publish("off", "Off. Audio could not be enabled.");
        return false;
      }
      try {
        if (context === attempt.context) {
          await rollbackContext(attempt.context, "Cleaning up failed audio enable...");
        } else {
          await rollbackAttempt(
            attempt,
            "Cleaning up failed audio enable...",
          );
        }
      } catch (cleanupError) {
        throw new AggregateError(
          [enableError, cleanupError],
          "Audio enable failed and cleanup is incomplete",
        );
      }
      return false;
    }
  };

  const enableFromGesture = (): Promise<boolean> => {
    if (currentSnapshot.status === "on") return Promise.resolve(true);
    if (currentSnapshot.status === "enabling" && enablePromise !== null) return enablePromise;
    if (currentSnapshot.status === "disabling") return Promise.resolve(false);

    const token = ++operation;
    let resolveEnable: (enabled: boolean) => void = () => undefined;
    let rejectEnable: (reason?: unknown) => void = () => undefined;
    const pending = new Promise<boolean>((resolve, reject) => {
      resolveEnable = resolve;
      rejectEnable = reject;
    });
    enablePromise = pending;
    publish("enabling", "Enabling...");
    if (operation !== token || currentSnapshot.status !== "enabling") {
      resolveEnable(false);
      if (enablePromise === pending) enablePromise = null;
      return pending;
    }
    void performEnable(token).then(resolveEnable, rejectEnable).finally(() => {
      if (enablePromise === pending) enablePromise = null;
    });
    return pending;
  };

  const performDisable = async (token: number): Promise<void> => {
    const candidate = cleanupContext;
    const progress = masterCleanup;
    if (candidate === null || progress === null) {
      throw new Error("Audio cleanup authority is unavailable");
    }

    const errors: unknown[] = [];
    cancelChimeSchedule(errors);
    const now = candidate.currentTime;
    const stopAt = now + RAMP_SECONDS;

    if (masterGain === null) {
      progress.cancelComplete = true;
      progress.setComplete = true;
      progress.rampComplete = true;
    } else {
      if (!progress.cancelComplete) {
        try {
          masterGain.gain.cancelScheduledValues(now);
          progress.cancelComplete = true;
        } catch (error) {
          errors.push(error);
        }
      }
      if (!progress.setComplete) {
        try {
          masterGain.gain.setValueAtTime(masterGain.gain.value, now);
          progress.setComplete = true;
        } catch (error) {
          errors.push(error);
        }
      }
      if (!progress.rampComplete) {
        try {
          masterGain.gain.linearRampToValueAtTime(0, stopAt);
          progress.rampComplete = true;
        } catch (error) {
          errors.push(error);
        }
      }
    }

    for (const source of [...sources]) {
      const oscillator = source as OscillatorNodeBoundary;
      if (chimeSources.has(oscillator)) oscillator.onended = null;
      if (!startedSources.has(source)) {
        sources.delete(source);
        chimeSources.delete(oscillator);
        continue;
      }
      try {
        source.stop(stopAt);
        sources.delete(source);
        chimeSources.delete(oscillator);
        startedSources.delete(source);
      } catch (error) {
        errors.push(error);
      }
    }

    if (!progress.waitComplete) {
      try {
        await waitForRamp(RAMP_SECONDS * 1_000);
        progress.waitComplete = true;
      } catch (error) {
        errors.push(error);
      }
    }

    disconnectOwnedNodes(errors);

    if (!cleanupContextClosed) {
      try {
        await candidate.close();
        cleanupContextClosed = true;
      } catch (error) {
        errors.push(error);
      }
    }

    if (errors.length > 0) {
      if (operation === token) {
        publish("disabling", "Cleanup incomplete. Retry cleanup.");
      }
      throw new AggregateError(errors, "Audio cleanup incomplete");
    }

    const complete = cleanupContextClosed
      && chimeTimers.size === 0
      && ownedNodes.size === 0
      && sources.size === 0
      && progress.cancelComplete
      && progress.setComplete
      && progress.rampComplete
      && progress.waitComplete;
    if (!complete) {
      if (operation === token) {
        publish("disabling", "Cleanup incomplete. Retry cleanup.");
      }
      throw new Error("Audio cleanup remains incomplete");
    }

    cleanupContext = null;
    cleanupContextClosed = false;
    masterCleanup = null;
    masterGain = null;
    layerGains.clear();
    chimeSources.clear();
    startedSources.clear();
    pendingChimeEvent = null;
    if (operation === token) publish("off", "Off");
  };

  const prepareCleanup = (candidate: AudioContextBoundary): void => {
    if (cleanupContext !== null && cleanupContext !== candidate) {
      throw new Error("Another audio context still owns cleanup authority");
    }
    if (cleanupContext === null) {
      cleanupContext = candidate;
      cleanupContextClosed = false;
    }
    context = context === candidate ? null : context;
    masterCleanup ??= {
      cancelComplete: masterGain === null,
      setComplete: masterGain === null,
      rampComplete: masterGain === null,
      waitComplete: masterGain === null,
    };
  };

  const startCleanup = (cleanup: () => Promise<void>): Promise<void> => {
    if (disablePromise !== null) return disablePromise;
    let resolveDisable: () => void = () => undefined;
    let rejectDisable: (reason?: unknown) => void = () => undefined;
    const pending = new Promise<void>((resolve, reject) => {
      resolveDisable = resolve;
      rejectDisable = reject;
    });
    disablePromise = pending;
    void cleanup().then(
      () => {
        if (disablePromise === pending) disablePromise = null;
        resolveDisable();
      },
      (error: unknown) => {
        if (disablePromise === pending) disablePromise = null;
        rejectDisable(error);
      },
    );
    return pending;
  };

  const rollbackContext = (
    candidate: AudioContextBoundary,
    message: string,
  ): Promise<void> => {
    if (disablePromise !== null) return disablePromise;
    prepareCleanup(candidate);
    const token = ++operation;
    publish("disabling", message);
    return startCleanup(() => performDisable(token));
  };

  const rollbackAttempt = (
    attempt: EnableAttempt,
    message: string,
  ): Promise<void> => {
    cleanupAttempt = attempt;
    if (disablePromise !== null) return disablePromise;
    const visible = context === null && cleanupContext === null;
    const token = visible ? ++operation : operation;
    if (visible) publish("disabling", message);
    return startCleanup(async () => {
      const errors: unknown[] = [];
      try {
        if (attempt.cancelled && !attempt.settlementComplete) {
          await attempt.settlementPromise;
        }
        if (!attempt.closeComplete) {
          await attempt.context.close();
          attempt.closeComplete = true;
        }
      } catch (error) {
        errors.push(error);
      }
      if (errors.length > 0) {
        if (visible && operation === token) {
          publish("disabling", "Cleanup incomplete. Retry cleanup.");
        }
        throw new AggregateError(errors, "Audio cleanup incomplete");
      }
      if (cleanupAttempt === attempt) cleanupAttempt = null;
      if (visible && operation === token) publish("off", "Off");
    });
  };

  const disable = (): Promise<void> => {
    if (disablePromise !== null) return disablePromise;
    if (
      currentSnapshot.status === "off"
      && context === null
      && cleanupContext === null
      && cleanupAttempt === null
    ) return Promise.resolve();

    const attempt = pendingAttempt;
    if (attempt !== null) attempt.cancelled = true;
    pendingAttempt = null;
    const candidate = context ?? cleanupContext;
    const candidateAttempt = cleanupAttempt ?? attempt;
    if (candidateAttempt !== null && candidate === null) {
      return rollbackAttempt(
        candidateAttempt,
        "Turning off...",
      );
    }
    if (candidate === null) {
      operation += 1;
      publish("off", "Off");
      return Promise.resolve();
    }
    return rollbackContext(candidate, "Turning off...");
  };

  const setLayerEnabled = (layer: AudioLayer, enabled: boolean): void => {
    if (!AUDIO_LAYERS.includes(layer)) throw new RangeError(`Unknown audio layer: ${layer}`);
    if (typeof enabled !== "boolean") throw new TypeError("Layer state must be boolean");
    if (layers[layer] === enabled) return;
    layers[layer] = enabled;

    const gain = layerGains.get(layer);
    if (gain !== undefined && context !== null) {
      const now = context.currentTime;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.linearRampToValueAtTime(enabled ? LAYER_LEVELS[layer] : 0, now + 0.04);
    }
    if (layer === "chimes") {
      if (enabled) {
        scheduleNextChime();
      } else {
        cancelChimeSchedule();
        const now = context?.currentTime ?? 0;
        for (const source of chimeSources) {
          if (startedSources.has(source) && safeStop(source, now)) {
            startedSources.delete(source);
          }
        }
      }
    }
    publish(currentSnapshot.status, currentSnapshot.message);
  };

  const subscribe = (
    listener: (snapshot: ProceduralAudioSnapshot) => void,
  ): (() => void) => {
    listeners.add(listener);
    try {
      listener(currentSnapshot);
    } catch (error) {
      listeners.delete(listener);
      throw error;
    }
    return (): void => {
      listeners.delete(listener);
    };
  };

  return Object.freeze({
    get snapshot(): ProceduralAudioSnapshot {
      return currentSnapshot;
    },
    enableFromGesture,
    disable,
    setLayerEnabled,
    subscribe,
  });
};
